import * as React from 'react';
import 'moment/locale/pt';
import { Notes } from '@/components/custom/notes';
import { Notifications } from '@/components/custom/notifications';
import usePermission from '@/hooks/usePermission';
import { NotificationsMessage } from '@/components/custom/notifications-message';
import { useTranslation } from 'react-i18next';
import { Search } from '@/components/custom/search';
import EnvironmentSelector from '@/components/custom/environment-selector';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { FiUser } from 'react-icons/fi';
import { PATHS } from '@/routes/paths';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';

/**
 * Header Principal
 * Gerencia o posicionamento fixo, padding e variações de altura.
 * Classes BEM: c-header
 */
const HeaderWrapper = React.forwardRef(({ className, hasCustomLogos, playful, ...props }, ref) => {
  const playfulDefault = usePlayfulStudent();
  const isPlayful = playful ?? playfulDefault;
  return (
    <header
      ref={ref}
      className={cn(
        'c-header-wrapper',
        'fixed inset-x-0 top-0 z-[104] flex items-center bg-neutral-bright',
        '!p-[4.2em_1rem_1rem_1rem]',
        isPlayful ? 'md:left-0 md:h-[88px] md:!p-[36px_0_0_0]' : 'md:left-[64px] md:h-[88px] md:!p-[36px_0_0_0]',
        hasCustomLogos ? 'top-[68px] md:top-0' : 'top-0',
        className
      )}
      {...props}
    />
  );
});
HeaderWrapper.displayName = 'HeaderWrapper';

/**
 * Header Container
 * Responsável pelo alinhamento interno e a borda decorativa inferior.
 * Classes BEM: c-header-container
 */
const HeaderContainer = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-header-container', 'flex w-full items-center justify-end', 'md:mx-[45px] md:h-[52px] md:border-b md:border-solid md:border-primary-lightest', className)}
    {...props}
  />
));
HeaderContainer.displayName = 'HeaderContainer';

/**
 * Header Actions
 * Agrupa as ações do lado direito do header.
 * Classes BEM: c-header-actions
 */
const HeaderActions = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-header-actions', 'mr-[36px] flex min-h-[28px] items-center gap-5', 'sm:gap-6', 'md:mr-0', className)}
    {...props}
  />
));
HeaderActions.displayName = 'HeaderActions';

export function Header({ hasCustomLogos, playfulNav }) {
  const { i18n } = useTranslation();

  React.useEffect(() => {
    i18n.changeLanguage(process.env.REACT_APP_LOCALE);
  }, []);

  const showNotes = usePermission('Criar anotações', 'Anotações') || usePermission('Editar anotações', 'Anotações') || usePermission('Excluir anotações', 'Anotações') || usePermission('Visualizar anotações', 'Anotações');

  // No tema playful a sidebar (que guardava a logo) sai; trazemos a logo da
  // marca pro canto superior esquerdo do header. mr-auto empurra as ações p/ direita.
  // Mesma convenção de dashboard-sidebar.jsx/LoginLayout.jsx: canaleducacao e
  // maiseducacao usam o arquivo logo-seduc-horizontal.png, os demais tenants usam horizontal-logo.svg.
  const appName = process.env.REACT_APP_NAME;
  const playfulDefault = usePlayfulStudent();
  const playful = playfulNav ?? playfulDefault;
  const playfulLogo = playful
    ? appName === 'canaleducacao' || appName === 'maiseducacao'
      ? require(`@/assets/images/${appName}/logo-seduc-horizontal.png`)
      : require(`@/assets/images/${appName}/horizontal-logo.svg`)
    : null;

  return (
    <HeaderWrapper
      hasCustomLogos={hasCustomLogos}
      playful={playful}
    >
      <HeaderContainer>
        {playful && (
          <img
            src={playfulLogo?.default ?? playfulLogo}
            alt="Logo"
            className="mr-auto h-9 w-auto shrink-0 object-contain"
          />
        )}
        <HeaderActions>
          <EnvironmentSelector />
          <Search />
          <NotificationsMessage />
          <Notifications />
          {playful && (
            <Link
              to={PATHS.PROFILE.ROOT}
              aria-label="Perfil"
              className="flex items-center text-secondary transition-colors hover:opacity-70"
            >
              <FiUser size={16} />
            </Link>
          )}
        </HeaderActions>

        {showNotes && <Notes hasCustomLogos={hasCustomLogos} />}
      </HeaderContainer>
    </HeaderWrapper>
  );
}
